import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal.js';
import { Button } from './Button.js';

// ── Component ─────────────────────────────────────────────────────────────────

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'warning' | 'cyan';
  loading?: boolean;
  /**
   * Tercera salida, entre cancelar y confirmar. Existe para la pregunta que no
   * es sí/no sino "¿todos o solo este?": sin ella, cada pantalla que la
   * necesita se escribe su propio modal y el diálogo deja de ser uno solo.
   * Ambas van juntas o no va ninguna.
   */
  secondaryLabel?: string;
  onSecondary?: () => void | Promise<void>;
}

const TONE_MAP = {
  danger:  'danger',
  warning: 'amber',
  cyan:    'cyan',
} as const;

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  loading = false,
  secondaryLabel,
  onSecondary,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.confirm');
  const resolvedCancelLabel  = cancelLabel  ?? t('common.cancel');
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  const handleSecondary = async () => {
    setBusy(true);
    try {
      await onSecondary?.();
    } finally {
      setBusy(false);
    }
  };

  const isLoading = loading || busy;
  const haySecundaria = secondaryLabel != null && onSecondary != null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      {...(description != null ? { description } : {})}
      size="sm"
      preventClose={isLoading}
      footer={
        <>
          <Button
            variant="outline"
            tone="muted"
            size="sm"
            disabled={isLoading}
            onClick={onClose}
          >
            {resolvedCancelLabel}
          </Button>
          {haySecundaria && (
            <Button
              variant="outline"
              tone={TONE_MAP[tone]}
              size="sm"
              disabled={isLoading}
              onClick={() => void handleSecondary()}
            >
              {secondaryLabel}
            </Button>
          )}
          <Button
            variant="solid"
            tone={TONE_MAP[tone]}
            size="sm"
            disabled={isLoading}
            loading={isLoading}
            onClick={() => void handleConfirm()}
          >
            {resolvedConfirmLabel}
          </Button>
        </>
      }
    >
      {/* empty body — title + description carry the message */}
      <div style={{ height: 0 }} />
    </Modal>
  );
}

// ── useConfirm ────────────────────────────────────────────────────────────────

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmDialogProps['tone'];
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve: ((result: boolean) => void) | null;
}

export function useConfirm() {
  const [state, setState] = useState<ConfirmState>({
    open: false,
    title: '',
    resolve: null,
  });
  const resolveRef = useRef<((result: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setState({ ...opts, open: true, resolve });
    });
  }, []);

  const handleClose = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setState(s => ({ ...s, open: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setState(s => ({ ...s, open: false }));
  }, []);

  const dialog = (
    <ConfirmDialog
      open={state.open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={state.title}
      {...(state.description  != null ? { description:  state.description  } : {})}
      {...(state.confirmLabel != null ? { confirmLabel: state.confirmLabel } : {})}
      {...(state.cancelLabel  != null ? { cancelLabel:  state.cancelLabel  } : {})}
      {...(state.tone         != null ? { tone:         state.tone         } : {})}
    />
  );

  return { confirm, dialog };
}
